import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { PRIORITY_LEVELS, PRIORITY_LABELS } from '../../constants/cardConstants';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useClickOutside } from '../../hooks/useClickOutside';
import CloseIcon from '../icons/CloseIcon'; // Import CloseIcon

const FilterPopoverContainer = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: #282E33;
    border-radius: 3px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    padding: 12px;
    width: 320px; /* Increased width to accommodate new checkboxes */
    z-index: 1030; /* Increased z-index */
    margin-top: 4px;
    
    @media (max-width: 768px) {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 90%;
        max-width: 360px; /* Increased max-width */
        z-index: 1032; /* Increased z-index for mobile */
    }
`;

const ClearAllButton = styled.button`
  width: 100%;
  padding: 10px;
  background: #3A3F44;
  color: #B6C2CF;
  border: none;
  border-radius: 3px;
  margin-top: 16px;
  cursor: pointer;

  &:hover {
    background: #4A4F54;
  }
`;

const FilterHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const SectionTitle = styled.h4`
    margin: 0 0 8px 0;
    color: #B6C2CF;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
`;

const PopoverCloseButton = styled.button` /* Renamed to avoid conflict */
    background: none;
    border: none;
    color: #B6C2CF;
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 3px;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const FilterSection = styled.div`
    margin-bottom: 16px;
`;

const FilterOption = styled.div`
    padding: 8px;
    border-radius: 3px;
    /* cursor: pointer; */ /* Handled by individual checkboxes now */
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #B6C2CF;
    font-size: 14px;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05); /* Lighter hover for the row */
    }
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
`;

const Checkbox = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid #B6C2CF;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-left: 8px; /* Space between exclude and include checkboxes */

    &:first-child {
        margin-left: 0;
    }

    /* Default border colors */
    ${props => props.type === 'include' && `
        border-color: green;
    `}

    ${props => props.type === 'exclude' && `
        border-color: red;
        svg {
            /* margin-right: 0; */ /* This was originally requested, but CloseIcon has no margin-right by default */
        }
    `}
    
    /* Checked styles - these will override the defaults */
    ${props => props.checked && props.type === 'include' && `
        background: #579DFF;
        border-color: #579DFF; /* Blue when checked */
        
        &:after {
            content: '✓';
            color: white;
            font-size: 12px;
        }
    `}

    ${props => props.checked && props.type === 'exclude' && `
        border-color: #FF6B6B; /* Ensure this is the stronger red for checked state */
        background: transparent; /* Or some other indicator if needed */
        svg {
            stroke: #FF6B6B; /* Red cross icon */
            margin-right: 0;
        }
    `}
`;

const PriorityIndicator = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: ${props => {
        if (props.priority === 'none') return '#B6C2CF';
        return PRIORITY_LABELS[props.priority]?.color || '#B6C2CF';
    }};
`;

const LabelIndicator = styled.div`
    width: 32px;
    height: 8px;
    border-radius: 4px;
    margin-right: 8px;
    background-color: ${props => props.color || '#B6C2CF'};
`;

const OptionContent = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1; /* Allow content to take available space */
    margin-right: 8px; /* Space before checkboxes */
`;

const FilterPopover = ({ 
    onClose, 
    labels = [], 
    members = {}, 
    filters, 
    onFilterChange 
}) => {
    const popoverRef = useRef(null);
    useClickOutside(popoverRef, onClose);

    const [isPriorityCollapsed, setIsPriorityCollapsed] = useLocalStorage('project-filter-priority-collapsed', false);
    const [isLabelsCollapsed, setIsLabelsCollapsed] = useLocalStorage('project-filter-labels-collapsed', true);
    const [isAssigneeCollapsed, setIsAssigneeCollapsed] = useLocalStorage('project-filter-assignee-collapsed', true);
    const [isDueDateCollapsed, setIsDueDateCollapsed] = useLocalStorage('project-filter-duedate-collapsed', true);

    // Note: selectedDueDateFilters from useLocalStorage is removed here.
    // Due date filters will now be part of the main `filters` object.

    const priorities = [
        { id: PRIORITY_LEVELS.IMMEDIATE, name: PRIORITY_LABELS[PRIORITY_LEVELS.IMMEDIATE].label },
        { id: PRIORITY_LEVELS.HIGH, name: PRIORITY_LABELS[PRIORITY_LEVELS.HIGH].label },
        { id: PRIORITY_LEVELS.NORMAL, name: PRIORITY_LABELS[PRIORITY_LEVELS.NORMAL].label },
        { id: PRIORITY_LEVELS.LOW, name: PRIORITY_LABELS[PRIORITY_LEVELS.LOW].label },
        { id: 'none', name: 'None' }
    ];

    const dueDateOptions = [
        { id: 'no_due_date', name: 'No due date' },
        { id: 'overdue', name: 'Overdue' },
        { id: 'today', name: 'Due today' },
        { id: 'this_week', name: 'Due this week' },
        { id: 'next_week', name: 'Due next week' },
    ];

    const handleToggleFilter = (filterType, id, newCheckedState, operationType) => {
        const currentFilters = JSON.parse(JSON.stringify(filters)); // Deep copy

        // Ensure filterType exists and has include/exclude arrays
        if (!currentFilters[filterType]) {
            currentFilters[filterType] = { include: [], exclude: [] };
        }
        if (!currentFilters[filterType].include) currentFilters[filterType].include = [];
        if (!currentFilters[filterType].exclude) currentFilters[filterType].exclude = [];

        const { include, exclude } = currentFilters[filterType];

        if (newCheckedState) { // If checking a box
            if (operationType === 'include') {
                if (!include.includes(id)) include.push(id);
                currentFilters[filterType].exclude = exclude.filter(item => item !== id); // Uncheck exclude
            } else { // operationType === 'exclude'
                if (!exclude.includes(id)) exclude.push(id);
                currentFilters[filterType].include = include.filter(item => item !== id); // Uncheck include
            }
        } else { // If unchecking a box
            if (operationType === 'include') {
                currentFilters[filterType].include = include.filter(item => item !== id);
            } else { // operationType === 'exclude'
                currentFilters[filterType].exclude = exclude.filter(item => item !== id);
            }
        }
        onFilterChange(currentFilters);
    };

    // handleToggleDueDateFilter is now covered by handleToggleFilter by passing 'dueDate' as filterType

    const clearAllFilters = () => {
        // Reset all filter types to empty include/exclude arrays
        const clearedFilters = {};
        ['priority', 'labels', 'assignees', 'dueDate'].forEach(type => {
            clearedFilters[type] = { include: [], exclude: [] };
        });
        onFilterChange(clearedFilters);
    };

    // Helper to check if a filter is included or excluded
    const isFilterActive = (type, id, operation) => {
        return filters[type]?.[operation]?.includes(id) || false;
    };

    return (
        <FilterPopoverContainer ref={popoverRef} onMouseDown={e => e.stopPropagation()}>
            <FilterHeader>
                <SectionTitle>Filter Cards</SectionTitle>
                <PopoverCloseButton onClick={onClose}>×</PopoverCloseButton>
            </FilterHeader>
            
            <FilterSection>
                <SectionTitle onClick={() => setIsPriorityCollapsed(!isPriorityCollapsed)}>
                    <span>Priority</span>
                    <span>{isPriorityCollapsed ? '▶' : '▼'}</span>
                </SectionTitle>
                {!isPriorityCollapsed && (
                    <>
                        {priorities.map(priority => (
                            <FilterOption key={priority.id}>
                                <OptionContent>
                                    <PriorityIndicator priority={priority.id} />
                                    {priority.name}
                                </OptionContent>
                                <CheckboxContainer>
                                    <Checkbox
                                        type="exclude"
                                        checked={isFilterActive('priority', priority.id, 'exclude')}
                                        onClick={() => handleToggleFilter('priority', priority.id, !isFilterActive('priority', priority.id, 'exclude'), 'exclude')}
                                    >
                                        {isFilterActive('priority', priority.id, 'exclude') && <CloseIcon />}
                                    </Checkbox>
                                    <Checkbox
                                        type="include"
                                        checked={isFilterActive('priority', priority.id, 'include')}
                                        onClick={() => handleToggleFilter('priority', priority.id, !isFilterActive('priority', priority.id, 'include'), 'include')}
                                    />
                                </CheckboxContainer>
                            </FilterOption>
                        ))}
                    </>
                )}
            </FilterSection>

            <FilterSection>
                <SectionTitle onClick={() => setIsDueDateCollapsed(!isDueDateCollapsed)}>
                    <span>Due Date</span>
                    <span>{isDueDateCollapsed ? '▶' : '▼'}</span>
                </SectionTitle>
                {!isDueDateCollapsed && (
                    <>
                        {dueDateOptions.map(option => (
                            <FilterOption key={option.id}>
                                <OptionContent>
                                    {option.name}
                                </OptionContent>
                                <CheckboxContainer>
                                     <Checkbox
                                        type="exclude"
                                        checked={isFilterActive('dueDate', option.id, 'exclude')}
                                        onClick={() => handleToggleFilter('dueDate', option.id, !isFilterActive('dueDate', option.id, 'exclude'), 'exclude')}
                                    >
                                        {isFilterActive('dueDate', option.id, 'exclude') && <CloseIcon />}
                                    </Checkbox>
                                    <Checkbox
                                        type="include"
                                        checked={isFilterActive('dueDate', option.id, 'include')}
                                        onClick={() => handleToggleFilter('dueDate', option.id, !isFilterActive('dueDate', option.id, 'include'), 'include')}
                                    />
                                </CheckboxContainer>
                            </FilterOption>
                        ))}
                    </>
                )}
            </FilterSection>
            
            <FilterSection>
                <SectionTitle onClick={() => setIsLabelsCollapsed(!isLabelsCollapsed)}>
                    <span>Labels</span>
                    <span>{isLabelsCollapsed ? '▶' : '▼'}</span>
                </SectionTitle>
                {!isLabelsCollapsed && (
                    <>
                        {labels.length === 0 ? (
                            <div style={{ color: '#8C9BAB', fontSize: '14px', padding: '8px' }}>
                                No labels available
                            </div>
                        ) : (
                            labels.map(label => (
                                <FilterOption key={label.id}>
                                    <OptionContent>
                                        <LabelIndicator color={label.color} />
                                        {label.title}
                                    </OptionContent>
                                    <CheckboxContainer>
                                        <Checkbox
                                            type="exclude"
                                            checked={isFilterActive('labels', label.id, 'exclude')}
                                            onClick={() => handleToggleFilter('labels', label.id, !isFilterActive('labels', label.id, 'exclude'), 'exclude')}
                                        >
                                            {isFilterActive('labels', label.id, 'exclude') && <CloseIcon />}
                                        </Checkbox>
                                        <Checkbox
                                            type="include"
                                            checked={isFilterActive('labels', label.id, 'include')}
                                            onClick={() => handleToggleFilter('labels', label.id, !isFilterActive('labels', label.id, 'include'), 'include')}
                                        />
                                    </CheckboxContainer>
                                </FilterOption>
                            ))
                        )}
                    </>
                )}
            </FilterSection>
            
            <FilterSection>
                <SectionTitle onClick={() => setIsAssigneeCollapsed(!isAssigneeCollapsed)}>
                    <span>Assignee</span>
                    <span>{isAssigneeCollapsed ? '▶' : '▼'}</span>
                </SectionTitle>
                {!isAssigneeCollapsed && (
                    <>
                        {Object.keys(members).length === 0 ? (
                            <div style={{ color: '#8C9BAB', fontSize: '14px', padding: '8px' }}>
                                No members available
                            </div>
                        ) : (
                            Object.entries(members).map(([userId, memberData]) => (
                                <FilterOption key={userId}>
                                    <OptionContent>
                                        {memberData?.displayName || memberData?.email || 'Unknown User'}
                                    </OptionContent>
                                    <CheckboxContainer>
                                        <Checkbox
                                            type="exclude"
                                            checked={isFilterActive('assignees', userId, 'exclude')}
                                            onClick={() => handleToggleFilter('assignees', userId, !isFilterActive('assignees', userId, 'exclude'), 'exclude')}
                                        >
                                            {isFilterActive('assignees', userId, 'exclude') && <CloseIcon />}
                                        </Checkbox>
                                        <Checkbox
                                            type="include"
                                            checked={isFilterActive('assignees', userId, 'include')}
                                            onClick={() => handleToggleFilter('assignees', userId, !isFilterActive('assignees', userId, 'include'), 'include')}
                                        />
                                    </CheckboxContainer>
                                </FilterOption>
                            ))
                        )}
                    </>
                )}
            </FilterSection>
            <ClearAllButton onClick={clearAllFilters}>
                Clear All Filters
            </ClearAllButton>
        </FilterPopoverContainer>
    );
};

export default FilterPopover;